import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Select } from 'antd';
import { MinusOutlined } from '@ant-design/icons';
import { Crop } from '@types';
import { Option } from './DealerForm';
import cls from './DealerForm.module.css';
import { clsx } from 'clsx';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre', 'Todo el año'];

const initialCrop = {
  crop: '', ibm: [],
};

interface UniqueCropsProps {
  crops: Crop[];
  record: any;
  states: Option[];
}

export const UniqueCrops = ({ crops, record, states }: UniqueCropsProps) => {
  const { setValue, watch } = useFormContext();
  const cropsIbm = watch('cropsIbm') || [];

  useEffect(() => {
    if (!record?.id) {
      const currentValue = watch('cropsIbm');
      if (!currentValue || currentValue.length === 0) {
        setValue('cropsIbm', [initialCrop]);
      }
      return;
    }

    if (record?.cropsIbm && Array.isArray(record.cropsIbm) && record.cropsIbm.length > 0) {
      const formattedCropsIbm = record.cropsIbm.map((item: any) => ({
        crop: item.crop || '',
        ibm: Array.isArray(item.ibm) ? item.ibm : (item.ibm ? item.ibm.split(',').map((i: string) => i.trim()) : []),
      }));
      setValue('cropsIbm', formattedCropsIbm);
    } else {
      setValue('cropsIbm', []);
    }
  }, [record?.id, record?.cropsIbm, setValue, watch]);

  const addCropsIbmHandler = () => {
    if (hasEmptyCrops) {
      return;
    }

    const currentCropIbms = cropsIbm || [];
    setValue('cropsIbm', [...currentCropIbms, initialCrop]);
  };

  const removeCropsIbmHandler = (index: number) => {
    const newCrops = cropsIbm.filter((_, i) => i !== index);
    setValue('cropsIbm', newCrops);
  };

  const updateCropsIbmHandler = (index: number, value: string) => {
    const newCrops = [...cropsIbm];
    newCrops[index] = { ...newCrops[index], crop: value };
    setValue('cropsIbm', newCrops);
  };

  const updateUniqueCropsIBMHandler = (index: number, value: string[]) => {
    const newCrops = [...cropsIbm];
    newCrops[index] = { ...newCrops[index], ibm: value };
    setValue('cropsIbm', newCrops);
  };

  const updateHarvestMonthHandler = (index: number, value: string[]) => {
    const newCrops = [...cropsIbm];
    newCrops[index] = { ...newCrops[index], harvestMonth: value };
    setValue('cropsIbm', newCrops);
  };

  const updateStatesHandler = (index: number, value: string[]) => {
    const newCrops = [...cropsIbm];
    newCrops[index] = { ...newCrops[index], states: value };
    setValue('cropsIbm', newCrops);
  };

  const hasEmptyCrops = cropsIbm.some(({ crop }) => crop === '');

  return (
    <div className={cls.crops}>
      <div className={cls.cropsTitle}>Unique Crops</div>
      <div className={cls.cropsTable}>
        <div className={cls.cropsTableHead}>
          <div className={cls.cropsTableCol}>Crop name</div>
          <div className={cls.cropsTableCol}>IBM</div>
          <div className={cls.cropsTableCol}>Harvest Month</div>
          <div className={cls.cropsTableCol}>States</div>
        </div>
        <div>
          {cropsIbm.map((cropIbm, index) => (
            <div key={`crop-${index}`} className={cls.cropsTableRow}>
              <div className={cls.cropsTableRemove} onClick={() => removeCropsIbmHandler(index)}>
                <MinusOutlined />
              </div>
              <Select
                className={cls.cropsTableCol}
                onChange={(value) => updateCropsIbmHandler(index, value)}
                options={crops
                  .filter(crop => cropsIbm
                    .every((item, idx) => idx === index || item.crop !== crop.nameMx),
                  )
                  .map(crop => ({
                    label: crop.nameMx,
                    value: crop.nameMx,
                  }))}
                value={cropIbm.crop}
              />
              <Select
                className={clsx(cls.cropsTableCol, cls.months)}
                onChange={(value) => updateUniqueCropsIBMHandler(index, value)}
                options={MONTHS.map(ibm => ({
                  label: ibm,
                  value: ibm,
                }))}
                mode="multiple"
                value={cropIbm.ibm || []}
                maxTagCount={3}
              />
              <Select
                className={clsx(cls.cropsTableCol, cls.months)}
                onChange={(value) => updateHarvestMonthHandler(index, value)}
                options={MONTHS.map(ibm => ({
                  label: ibm,
                  value: ibm,
                }))}
                mode="multiple"
                value={cropIbm.harvestMonth || []}
                maxTagCount={3}
              />
              <Select
                className={clsx(cls.cropsTableCol, cls.months)}
                onChange={(value) => updateStatesHandler(index, value)}
                options={(states || []).map(state => ({
                  label: state.name,
                  value: state.name,
                }))}
                mode="multiple"
                value={cropIbm.states || []}
                maxTagCount={2}
              />
            </div>
          ))}
        </div>
        <div className={cls.cropsTableActions}>
          <Button
            className={cls.cropsTableAdd}
            htmlType="button"
            onClick={addCropsIbmHandler}
            disabled={hasEmptyCrops}
          >+</Button>
        </div>
      </div>
    </div>
  );
};
